import React, { useEffect, useState } from "react";
import { X, ShieldCheck, ShieldX, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Admin-only modal to view the uploaded driver's license (recto + verso) for
 * a pending withdrawal and either approve it or reject it.
 *
 * Rejection has two sub-actions:
 *  - "refund"  → reject the whole withdrawal & refund principal + fees
 *  - "retry"   → ask user to re-upload a new document
 */
export default function AdminIdReviewModal({ txId, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectMode, setRejectMode] = useState(false);
  const [reason, setReason] = useState("");
  const [rejectAction, setRejectAction] = useState("retry");

  useEffect(() => {
    if (!txId) return;
    let alive = true;
    (async () => {
      try {
        const r = await api.get(`/admin/withdrawals/${txId}/id-verification`);
        if (alive) setData(r.data);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not load ID");
        onClose && onClose();
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [txId, onClose]);

  if (!txId) return null;

  const approve = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/withdrawals/${txId}/id-verification/approve`, {});
      toast.success("ID verified — step 4 unlocked");
      onChange && onChange();
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/admin/withdrawals/${txId}/id-verification/reject`, {
        action: rejectAction,
        reason: reason.trim() || null,
      });
      if (r.data.withdrawal_rejected) {
        toast.success("Withdrawal rejected · user fully refunded");
      } else {
        toast.success("Document refused · user can re-upload");
      }
      onChange && onChange();
      onClose && onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(false);
    }
  };

  const status = data?.status || "awaiting_upload";
  const docType = data?.doc_type || "drivers_license";
  const isPassport = docType === "passport";
  const docTypeLabel = (
    { drivers_license: "Driver's license",
      passport: "Passport",
      national_id: "National ID card (EU)" }[docType] || docType
  );
  const canDecide = status === "awaiting_review";

  return (
    <div
      data-testid="admin-id-review-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#0B0D12] border border-[#2A2E38] w-full max-w-3xl my-8 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-[#141720] border-b border-[#2A2E38] px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#7C5CFF]">KYC · Step 3</div>
            <h2 className="font-display text-xl font-bold mt-1">
              Identity document verification
            </h2>
            <div className="text-xs text-[#A3A9B8] mt-1">
              Submitted as <span className="text-[#7C5CFF] font-medium">{docTypeLabel}</span>
            </div>
            <div className="text-xs text-[#6E7587] mt-1 font-mono">tx {txId}</div>
          </div>
          <button
            data-testid="admin-id-review-close"
            onClick={onClose}
            className="p-2 text-[#A3A9B8] hover:text-[#F4F1EB]"
          >
            <X size={18} />
          </button>
        </div>

        {loading && (
          <div className="p-10 text-center text-sm text-[#6E7587]">
            <Loader2 size={20} className="animate-spin inline-block mr-2" />
            Loading documents…
          </div>
        )}

        {!loading && data && (
          <>
            <div className="px-6 py-4 border-b border-[#2A2E38] grid grid-cols-2 md:grid-cols-3 gap-3 text-xs">
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">Status</div>
                <div className={`font-mono mt-1 ${
                  status === "approved" ? "text-[#7C9C69]"
                  : status === "rejected_final" || status === "rejected_retry" ? "text-[#C75C5C]"
                  : status === "awaiting_review" ? "text-[#D19B54]"
                  : "text-[#6E7587]"
                }`}>
                  {status}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">Uploaded</div>
                <div className="font-mono mt-1 text-[#A3A9B8]">
                  {data.uploaded_at ? new Date(data.uploaded_at).toLocaleString() : "—"}
                </div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587]">Reviewed</div>
                <div className="font-mono mt-1 text-[#A3A9B8]">
                  {data.reviewed_at ? new Date(data.reviewed_at).toLocaleString() : "—"}
                </div>
              </div>
            </div>

            {data.reject_reason && (
              <div className="mx-6 mt-4 p-3 border border-[#C75C5C]/40 bg-[#C75C5C]/5 text-xs text-[#A3A9B8]">
                <span className="text-[#C75C5C] font-medium">Previous reason: </span>
                {data.reject_reason}
              </div>
            )}

            <div className={`grid gap-4 p-6 ${isPassport ? "grid-cols-1" : "grid-cols-1 md:grid-cols-2"}`}>
              {(isPassport
                ? [{ label: "Bio-data page", b64: data.front_b64, key: "front" }]
                : [
                    { label: "Front (recto)", b64: data.front_b64, key: "front" },
                    { label: "Back (verso)", b64: data.back_b64, key: "back" },
                  ]
              ).map((doc) => (
                <div key={doc.key} className="border border-[#2A2E38] bg-[#0B0D12]">
                  <div className="text-[10px] tracking-[0.25em] uppercase text-[#6E7587] px-3 py-2 border-b border-[#2A2E38]">
                    {doc.label}
                  </div>
                  {doc.b64 ? (
                    <a
                      href={doc.b64}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`admin-id-${doc.key}-image`}
                    >
                      <img
                        src={doc.b64}
                        alt={doc.label}
                        className="w-full max-h-72 object-contain bg-black"
                      />
                    </a>
                  ) : (
                    <div className="h-48 flex items-center justify-center text-[#6E7587] text-xs">
                      No document uploaded yet
                    </div>
                  )}
                </div>
              ))}
            </div>

            {canDecide && (
              <div className="px-6 pb-6">
                {!rejectMode ? (
                  <div className="flex gap-3">
                    <button
                      data-testid="admin-id-approve"
                      onClick={approve}
                      disabled={busy}
                      className="flex-1 px-4 py-2.5 bg-[#7C9C69] text-white text-sm font-medium hover:bg-[#8FAF7B] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                      <ShieldCheck size={14} /> Approve & unlock step 4
                    </button>
                    <button
                      data-testid="admin-id-reject-open"
                      onClick={() => setRejectMode(true)}
                      disabled={busy}
                      className="flex-1 px-4 py-2.5 border border-[#C75C5C]/40 text-[#C75C5C] text-sm font-medium hover:bg-[#C75C5C]/10 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                    >
                      <ShieldX size={14} /> Reject document…
                    </button>
                  </div>
                ) : (
                  <div className="border border-[#C75C5C]/30 bg-[#C75C5C]/[0.05] p-4 space-y-3">
                    <div className="text-sm font-medium text-[#C75C5C]">Reject this document</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <button
                        data-testid="admin-id-reject-mode-retry"
                        type="button"
                        onClick={() => setRejectAction("retry")}
                        className={`text-left p-3 border text-xs transition-colors ${
                          rejectAction === "retry"
                            ? "border-[#7C5CFF] bg-[#7C5CFF]/10"
                            : "border-[#2A2E38] hover:border-[#7C5CFF]/60"
                        }`}
                      >
                        <div className="flex items-center gap-2 font-medium text-[#F4F1EB]">
                          <RefreshCw size={12} /> Ask for re-upload
                        </div>
                        <div className="text-[#A3A9B8] mt-1">
                          Funds stay locked · user must upload a new document.
                        </div>
                      </button>
                      <button
                        data-testid="admin-id-reject-mode-refund"
                        type="button"
                        onClick={() => setRejectAction("refund")}
                        className={`text-left p-3 border text-xs transition-colors ${
                          rejectAction === "refund"
                            ? "border-[#C75C5C] bg-[#C75C5C]/10"
                            : "border-[#2A2E38] hover:border-[#C75C5C]/60"
                        }`}
                      >
                        <div className="flex items-center gap-2 font-medium text-[#F4F1EB]">
                          <ShieldX size={12} /> Refuse & full refund
                        </div>
                        <div className="text-[#A3A9B8] mt-1">
                          Withdrawal rejected · principal + fees credited back.
                        </div>
                      </button>
                    </div>
                    <textarea
                      data-testid="admin-id-reject-reason"
                      value={reason}
                      onChange={(e) => setReason(e.target.value)}
                      placeholder="Reason shown to the user (optional)"
                      rows={3}
                      maxLength={300}
                      className="w-full bg-[#0B0D12] border border-[#2A2E38] text-sm px-3 py-2 focus:outline-none focus:border-[#7C5CFF] text-[#F4F1EB]"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setRejectMode(false)}
                        disabled={busy}
                        className="px-4 py-2 text-xs border border-[#2A2E38] text-[#A3A9B8] hover:border-[#7C5CFF] disabled:opacity-50"
                      >
                        Cancel
                      </button>
                      <button
                        data-testid="admin-id-reject-confirm"
                        type="button"
                        onClick={reject}
                        disabled={busy}
                        className="flex-1 px-4 py-2 text-xs bg-[#C75C5C] text-white hover:bg-[#D17070] disabled:opacity-50 inline-flex items-center justify-center gap-2"
                      >
                        {busy && <Loader2 size={12} className="animate-spin" />}
                        Confirm reject ({rejectAction === "refund" ? "refund" : "ask re-upload"})
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {!canDecide && (
              <div className="px-6 pb-6 text-xs text-[#6E7587]">
                {status === "awaiting_upload" && "The user has not uploaded their document yet."}
                {status === "approved" && "This document was already approved."}
                {status === "rejected_retry" && "The user was asked to re-upload — waiting for a new document."}
                {status === "rejected_final" && "Document was refused with a full refund. Withdrawal is closed."}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
